<template>
  <div>
    <ontos-instance-rel-input
        :property="property"
        :items="visibleItems"
        :value="selectValue"
        :readonly="readonly"
        :loading="loading" :disabled="loading"
        :force-multiple="isMultiple"
        :allow-remove="!protectedIds.length"
        :disabled-values="disabledValues"
        :error-message="errorMessage"
        :hint="blockedHint || hint"
        :creatable="creatable"
        @input="onInput"
        @create="openCreate"/>

    <create-instance-dialog v-if="createContext"
                            :context="createContext"
                            :initialName="createName"
                            :full-page="false"
                            :initialValues="createValues"
                            :deferred="isDeferred"
                            :omit-properties="isDeferred && reverseInfo ? [reverseInfo.reverse] : []"
                            @showMessage="$emit('showMessage', $event)"
                            @saved="onCreated"
                            @pending="onPending"
                            @closed="createContext = null"/>
  </div>
</template>
<script lang="ts">
import {defineAsyncComponent} from 'vue'
import {Component, Vue, Prop, toNative} from 'vue-facing-decorator'
import InstanceRelInput from "@/components/live/instance/InstanceRelInput.vue";
import IdeaProperty from "@/types/IdeaProperty";
import {asArray} from "@/utils/arrayUtils";
import {DomainItem, freeToLink, ReverseInfo} from "@/services/reverseRelationship";

/**
 * Input de relacionamento reverso. O valor mora nos documentos do outro lado, então
 * aqui só se escolhe quem aponta para esta instância — quem grava é o backend.
 * O valor trafega SEMPRE como array de ids; a adaptação para escalar acontece na
 * borda, porque o servidor serializa reverso sempre como array.
 */
@Component({
  emits: ['showMessage', 'input', 'change'],
  name: 'ontos-instance-reverse-rel-input',
  components: {
    'ontos-instance-rel-input': InstanceRelInput,
    //assíncrono para quebrar o ciclo: o dialog de criação renderiza este input
    'create-instance-dialog': defineAsyncComponent(() => import('./CreateInstanceDialog.vue'))
  }
})
class InstanceReverseRelInput extends Vue {
  @Prop({type: Object, required: true}) readonly property!: IdeaProperty
  @Prop() readonly value!: any
  @Prop({type: Array, default: () => []}) readonly items!: DomainItem[]
  @Prop() readonly reverseInfo!: ReverseInfo | undefined
  @Prop({type: String, default: ""}) readonly ideaAlias!: string
  @Prop({default: null}) readonly instanceId!: number | null
  @Prop({type: Boolean, default: false}) readonly readonly!: boolean
  /**
   * A prévia vai mudar as opções (ver o `InstanceInput.affectedBy`): progresso e travado. Por prop porque a
   * raiz aqui é a `div` — no `InstanceRelInput` ela desce sozinha, pelo `$attrs`, ao `v-autocomplete`.
   */
  @Prop({type: Boolean, default: false}) readonly loading!: boolean
  @Prop({type: String, default: ""}) readonly errorMessage!: string
  /** A orientação de quem usa o campo — a das "Opções permitidas". A trava, quando há, vem antes. */
  @Prop({type: String, default: ""}) readonly hint!: string
  @Prop({type: String, default: ""}) readonly systemAlias!: string

  createContext: any = null
  createName = ""
  createValues: any = {}
  //instâncias montadas que só podem nascer depois que esta existir
  pending: any[] = []
  private nextTempId = -1

  //quem já estava vinculado quando o formulário abriu
  originalIds: number[] = []

  created() {
    this.originalIds = asArray(this.value).map(Number)
  }

  //enquanto esta instância não existe, não há para onde o outro lado apontar
  get isDeferred(): boolean {
    return this.instanceId === null
  }

  //sem metadados do servidor não dá para saber cardinalidade: assume múltiplo
  get isMultiple(): boolean {
    return !this.reverseInfo || this.reverseInfo.multiple
  }

  //remover deixaria o documento do outro lado sem um campo obrigatório dele próprio
  get blockRemoval(): boolean {
    return !!this.reverseInfo && this.reverseInfo.ownerRequired
  }

  //o campo dono compõe a chave do outro lado: trocar aqui mudaria a identidade dele
  get blockEdit(): boolean {
    return !!this.reverseInfo && this.reverseInfo.ownerIdentifier
  }

  get currentIds(): number[] {
    return asArray(this.value).map(Number)
  }

  /**
   * O que não pode ser desmarcado. Só o que JÁ estava vinculado a esta instância:
   * desmarcar isso deixaria o outro lado sem um campo obrigatório dele. O que foi
   * marcado agora pertence a outra instância (ou nem existe ainda), então desmarcar
   * apenas desfaz a alteração pendente e não viola nada.
   */
  get protectedIds(): number[] {
    //chave: qualquer vínculo real já gravado é intocável; pendente ainda dá para tirar
    if (this.blockEdit) return this.currentIds.filter(id => id >= 0)
    if (this.blockRemoval) return this.originalIds
    return []
  }

  get disabledValues(): number[] {
    return this.protectedIds
  }

  /**
   * Auto-referência (pessoa.filhos lista pessoas): ninguém é filho de si mesmo.
   * E quando o campo dono compõe a chave, o servidor manda em _identifiers um
   * identificador sem a parte que aponta de volta para cá — repetir "Cálculo I"
   * em toda linha da lista de atividades de Cálculo I não distingue nada.
   */
  get visibleItems(): DomainItem[] {
    const info = this.reverseInfo
    let items = this.items
    //Chave travada: os não vinculados nem entram na lista, porque não há como
    //escolhê-los. Os já vinculados continuam visíveis, apenas desabilitados —
    //e são também o que dá rótulo aos chips, sem o que apareceria o id cru.
    if (this.blockEdit) {
      const current = this.currentIds
      items = items.filter(item => current.indexOf(Number(item._instance)) >= 0)
    }
    if (info && info.idea === this.ideaAlias && this.instanceId !== null) {
      items = items.filter(item => Number(item._instance) !== Number(this.instanceId))
    }
    //O que já é de outro nem se oferece, quando o campo de lá é de um valor só: não se rouba (ver o `freeToLink`)
    if (info) items = items.filter(item => freeToLink(item, info, this.instanceId))
    if (!info) return items
    return items.map(item => {
      const trimmed = item._identifiers && item._identifiers[info.reverse]
      return trimmed === undefined || trimmed === null ? item : {...item, _identifier: trimmed}
    })
  }

  get selectValue(): any {
    if (this.isMultiple) return this.currentIds
    return this.currentIds.length ? this.currentIds[0] : null
  }

  /**
   * Por que algo aqui não se desmarca. No `hint` do próprio campo, e não num `v-messages` solto
   * embaixo dele — que ficava fora do espaço de mensagens do Vuetify, longe do campo. A chave é
   * motivo mais forte que a obrigatoriedade: bloqueia tudo, não só remover.
   */
  get blockedHint(): string {
    //com a lista vazia não há restrição a explicar: as dicas falam do que já está lá
    if (!this.reverseInfo || !this.currentIds.length) return ""
    if (this.blockEdit) {
      return this.$tt('reverse-identifier-blocked', {
        property: this.reverseInfo.ownerName,
        idea: this.reverseInfo.ownerIdeaName
      })
    }
    if (this.blockRemoval) {
      return this.$tt('reverse-remove-blocked', {
        property: this.reverseInfo.ownerName,
        idea: this.reverseInfo.ownerIdeaName
      })
    }
    return ""
  }

  /**
   * Criar do lado reverso significa criar uma instância do OUTRO lado já apontando
   * para esta. Só faz sentido se der para deixá-la válida: quando o campo dono é
   * obrigatório, é preciso preenchê-lo, e para isso esta instância já tem que existir.
   */
  get creatable(): boolean {
    //blockEdit não impede: nascer vinculado não muda a identidade de ninguém.
    //Também não se exige que esta instância já exista — o dialog do outro lado
    //mostra os campos dele e o usuário associa o que quiser por lá.
    return !this.readonly && !!this.reverseInfo
  }

  openCreate(text: string) {
    if (!this.reverseInfo) return
    this.createName = text
    //já nasce apontando para cá, senão o servidor recusaria por campo obrigatório
    this.createValues = this.isDeferred ? {}
        : {[this.reverseInfo.reverse]: this.ownerValueFor(this.instanceId)}
    this.createContext = {system: this.systemAlias, idea: this.reverseInfo.idea}
  }

  /** Segunda etapa: guarda os dados e mostra um chip provisório, com id negativo. */
  onPending(payload: any) {
    this.createContext = null
    if (!payload || !payload.data || !this.reverseInfo) return
    const tempId = this.nextTempId--
    //mediaAliases vem do dialog: só ele carregou a idea do outro lado e sabe quais
    //propriedades são mídia, e elas precisam subir depois de a instância nascer
    this.pending.push({tempId, data: payload.data, mediaAliases: payload.mediaAliases})
    //o dialog resolve o identificador pelo domínio dele; aqui só há ids
    this.items.push({_instance: tempId, _identifier: payload.identifier || this.createName})
    const next = [...this.currentIds, tempId]
    this.$emit('input', next)
    this.$emit('change', next)
  }

  /** Lido pelo InstanceInput na hora de salvar, para criar o que ficou pendente. */
  pendingItems() {
    if (!this.reverseInfo) return []
    const info = this.reverseInfo
    return this.pending.map(p => ({
      ideaAlias: info.idea,
      ownerField: info.reverse,
      ownerMultiple: info.ownerMultiple,
      data: p.data,
      mediaAliases: p.mediaAliases
    }))
  }

  /** o campo dono guarda escalar ou array conforme a cardinalidade dele */
  private ownerValueFor(id: number | null) {
    return this.reverseInfo!.ownerMultiple ? [Number(id)] : Number(id)
  }

  onCreated(created: any) {
    this.createContext = null
    if (!created || created._instance === undefined || !this.reverseInfo) return
    //_identifiers junto: é dele que sai o rótulo sem a parte da chave que aponta de volta
    //para cá. Sem ele o chip da recém-criada nascia com o identificador cheio — "27/09 -
    //Psicolinguística" — enquanto as vizinhas, que vieram do domínio, mostravam só a data.
    this.items.push({
      _instance: created._instance,
      _identifier: created._identifier,
      _identifiers: created._identifiers
    })

    //Só entra na seleção se a instância criada realmente apontar para esta. Quando
    //ainda não existimos (criação), ou quando o usuário associou a outra instância
    //dentro do dialog, ela pertence a outro dono e não cabe nesta lista — insistir
    //faria o servidor recusar, já que mexer no campo dono é o que ele barra.
    const owners = asArray(created[this.reverseInfo.reverse]).map(Number)
    if (this.instanceId === null || owners.indexOf(Number(this.instanceId)) < 0) return

    const next = Array.from(new Set([...this.currentIds, Number(created._instance)]))
    this.$emit('input', next)
    this.$emit('change', next)
  }

  onInput($event: any) {
    let next = asArray($event).map(Number)
    //rede de segurança além do item desabilitado: repõe só o que é protegido, deixando
    //sair o que foi marcado nesta sessão
    const missing = this.protectedIds.filter(id => next.indexOf(id) < 0)
    if (missing.length) next = [...next, ...missing]
    this.$emit('input', next)
    this.$emit('change', next)
  }
}

export default toNative(InstanceReverseRelInput)
</script>
